/**
 * Insights — Value Realized page
 *
 * R3 P1 fix 2026-05-13 — PR #430 added `/insights-module/value` backend
 * (tasksAutomated, queriesHandled, hoursSaved, topUseCases) but no UI
 * consumed it. Visiting /insights/value 404'd in the SPA. This page
 * surfaces that endpoint as the ROI hero called out in
 * INSIGHTS-UI-SPEC §5.7.
 *
 * INSIGHTS-UI-SPEC ships 5 tabs (Overview / Adoption / Quality & Safety
 * / Cost & Capacity / Agents & Knowledge); Value is the spec's "ROI
 * card" surface. This page renders that surface in full, and the
 * Overview tab now ALSO surfaces hoursSaved on its "Estimated value
 * (7d)" card so the spec's small-card position is honoured too.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  TrendingUp, Clock, CheckCircle2, MessageSquare, Activity,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  KpiCard, ChartCard, FilterBar, ActionItemsDrawer, EmptySection,
  filtersToParams,
  useInsightsSearchFilters, validateInsightsSearch,
} from '@/components/insights';
import { useInsightsValue } from '@/lib/use-insights';

export const Route = createFileRoute('/insights/value')({
  component: ValueTab,
  // INS-FILTER-PERSIST-001 — URL-bind filter state per resolved decision #9.
  validateSearch: validateInsightsSearch,
});

function ValueTab() {
  const [filters, setFilters] = useInsightsSearchFilters('/insights/value');
  const [actionItemsOpen, setActionItemsOpen] = useState(false);

  // R3-P0 defect #1 — FilterBar filters propagate to backend.
  const filterParams = filtersToParams(filters);
  const range = filterParams.range;
  const { data: value, isLoading } = useInsightsValue(range, {
    department: filterParams.department,
    agentId: filterParams.agentId,
    modelId: filterParams.modelId,
  });

  const hoursSaved = value?.hoursSaved ?? 0;
  const tasksAutomated = value?.tasksAutomated ?? 0;
  const queriesHandled = value?.queriesHandled ?? 0;
  const completionRate = value?.completionRate ?? 0;
  const topUseCases = value?.topUseCases ?? [];

  return (
    <div className="flex flex-col h-full">
      <FilterBar
        filters={filters}
        onChange={setFilters}
        actionItemsCount={0}
        onOpenActionItems={() => setActionItemsOpen(true)}
      />

      {/* Pod-KK #613 — explicit custom-range fallback notice. */}
      {filterParams.customFallback && (
        <div
          role="status"
          className="mx-6 mt-3 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
        >
          Custom date ranges aren't supported yet — showing the last 7
          days. Pick a preset range to filter precisely.
        </div>
      )}

      <div className="flex-1 p-6 space-y-6">
        {/* Hero ROI card per INSIGHTS-UI-SPEC §5.7 */}
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-6">
            <div className="flex items-baseline gap-3">
              <div className="text-3xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                {isLoading ? '—' : hoursSaved.toLocaleString(undefined, { maximumFractionDigits: 1 })}
              </div>
              <div className="text-lg font-medium text-muted-foreground">hours saved</div>
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              over the last {range} via AI-deflected queries and completed
              agent jobs
            </div>
            <div className="text-[10px] italic text-muted-foreground mt-2">
              Methodology: minutes-per-query × queries handled + minutes-per-
              agent-job × tasks automated. Per-tenant factors configurable at
              /admin/settings (tenant_value_config).
            </div>
          </CardContent>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label="Tasks automated"
            icon={CheckCircle2}
            value={isLoading ? '—' : tasksAutomated.toLocaleString()}
            numericValue={tasksAutomated}
            subtitle="Agent jobs completed"
          />
          <KpiCard
            label="Queries handled"
            icon={MessageSquare}
            value={isLoading ? '—' : queriesHandled.toLocaleString()}
            numericValue={queriesHandled}
            subtitle="Counted in query_metrics"
          />
          <KpiCard
            label="Hours saved"
            icon={Clock}
            value={isLoading ? '—' : hoursSaved.toLocaleString(undefined, { maximumFractionDigits: 1 })}
            numericValue={hoursSaved}
            subtitle={`Methodology: time per task × volume (${range})`}
          />
          <KpiCard
            label="Completion rate"
            icon={Activity}
            value={isLoading ? '—' : `${completionRate.toFixed(1)}%`}
            numericValue={completionRate}
            subtitle="Completed / total agent jobs"
            statusDot={
              completionRate >= 95 ? 'emerald' : completionRate >= 85 ? 'amber' : 'red'
            }
          />
        </div>

        {/* Top use cases — by agent, per the /value endpoint shape */}
        <ChartCard title={`Top use cases (${range})`} icon={TrendingUp}>
          {topUseCases.length === 0 ? (
            <EmptySection
              message="No use-case data in the selected range."
              hint="Use cases are derived from query_metrics + agent_jobs, grouped by agent_id."
              minHeight="min-h-48"
            />
          ) : (
            <div className="space-y-1.5">
              {topUseCases.map((u, idx) => (
                <div
                  key={u.agentId ?? `unknown-${idx}`}
                  className="grid grid-cols-12 items-center gap-3 rounded-md border border-border bg-card px-3 py-2 text-xs"
                >
                  <div className="col-span-5 truncate">
                    <span className="font-medium">
                      {u.agentId ?? '(no agent)'}
                    </span>
                  </div>
                  <div className="col-span-2 tabular-nums text-right">
                    <span className="text-[10px] text-muted-foreground block">
                      Queries
                    </span>
                    {u.queryCount.toLocaleString()}
                  </div>
                  <div className="col-span-2 tabular-nums text-right">
                    <span className="text-[10px] text-muted-foreground block">
                      Completed
                    </span>
                    {u.tasksCompleted.toLocaleString()}
                  </div>
                  <div className="col-span-3 tabular-nums text-right">
                    <span className="text-[10px] text-muted-foreground block">
                      Hours saved
                    </span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {u.hoursSaved.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ChartCard>
      </div>

      <ActionItemsDrawer
        open={actionItemsOpen}
        onOpenChange={setActionItemsOpen}
        items={[]}
      />
    </div>
  );
}
